import React from 'react';
import { useLocalization, type Language } from '../../../../../config/localization';
import {
  DEFAULT_OUTGOING_IMAGE_POLICY,
  OUTGOING_IMAGE_BYTES_PER_MB,
  OUTGOING_IMAGE_MAX_BUDGET_MB,
  isOutgoingImagePolicyValid,
  type OutgoingImagePolicyDraft,
} from '../../../../../utils/outgoingImagePolicy';

interface Props {
  policy?: OutgoingImagePolicyDraft | null;
  onChange: (policy: OutgoingImagePolicyDraft) => void;
  language: Language;
}

export default function OutgoingImagePolicySection({ policy, onChange, language }: Props) {
  const t = useLocalization(language).admin.outgoingImages;
  const enabled = policy?.enabled === true;
  const draft = policy ?? { enabled: false };
  const update = (changes: Partial<OutgoingImagePolicyDraft>) => onChange({ ...draft, ...changes });
  const numeric = (value: string) => value === '' ? undefined : Number(value);
  const inputClass = 'w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm';

  return (
    <section className="border border-blue-500/30 rounded-xl p-4 space-y-3" aria-labelledby="outgoing-images-title">
      <h4 id="outgoing-images-title" className="font-bold text-blue-300">{t.title}</h4>
      <p className="text-sm text-gray-400">{t.description}</p>
      <label className="flex items-center gap-2 text-sm text-gray-300">
        <input type="checkbox" checked={enabled}
          onChange={event => onChange(event.target.checked
            ? { ...DEFAULT_OUTGOING_IMAGE_POLICY, ...draft, enabled: true }
            : { enabled: false })} />
        {t.enabled}
      </label>
      {enabled && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="text-sm text-gray-300">
              {t.format}
              <select className={inputClass} value={draft.output_format ?? ''}
                onChange={event => update({ output_format: event.target.value as 'jpeg' | 'webp' })}>
                <option value="jpeg">JPEG</option>
                <option value="webp">WebP</option>
              </select>
            </label>
            <label className="text-sm text-gray-300">
              {t.quality}
              <input type="number" className={inputClass} min={10} max={100} step={1} required
                value={draft.quality === undefined ? '' : Math.round(draft.quality * 100)}
                onChange={event => update({ quality: event.target.value === '' ? undefined : Number(event.target.value) / 100 })} />
            </label>
            <label className="text-sm text-gray-300">
              {t.perImage}
              <input type="number" className={inputClass} min={1 / OUTGOING_IMAGE_BYTES_PER_MB}
                max={OUTGOING_IMAGE_MAX_BUDGET_MB} step="any" required value={draft.max_image_size_mb ?? ''}
                onChange={event => update({ max_image_size_mb: numeric(event.target.value) })} />
            </label>
            <label className="text-sm text-gray-300">
              {t.total}
              <input type="number" className={inputClass} min={1 / OUTGOING_IMAGE_BYTES_PER_MB}
                max={OUTGOING_IMAGE_MAX_BUDGET_MB} step="any" required value={draft.max_total_size_mb ?? ''}
                onChange={event => update({ max_total_size_mb: numeric(event.target.value) })} />
            </label>
            <label className="text-sm text-gray-300">
              {t.dimension}
              <input type="number" className={inputClass} min={512} max={8192} step={1} required
                value={draft.max_dimension ?? ''}
                onChange={event => update({ max_dimension: numeric(event.target.value) })} />
            </label>
          </div>
          <p className="text-xs text-gray-400">{t.units}</p>
          <p className="text-xs text-gray-400">{t.base64}</p>
          <p className="text-xs text-gray-400">{t.transparency}</p>
          {!isOutgoingImagePolicyValid(draft) && <p role="alert" className="text-sm text-red-400">{t.invalid}</p>}
        </>
      )}
    </section>
  );
}
